import React, { useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Grow from "@mui/material/Grow";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { useGlobalState } from "../../GlobalStates";
import Button from "@mui/material/Button";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { currencyToCreditsGiftMap } from "../../utils/multi-language";
import { formatCurrency } from "../../utils/currency";

import "./index.scss";

const style = {
  position: "relative",
  width: "calc(100% - 2rem)",
  maxWidth: "29rem",
  bgcolor: "background.paper",
  borderRadius: "24px",
  p: 0,
  maxHeight: "calc(100dvh - 2rem)",
  overflowY: "auto",
  outline: "none",
  boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
};

export const LoginModal = () => {
  const { t } = useTranslation();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const duration = reduceMotion ? 0 : { enter: 360, exit: 260 };
  const anchorTransition = (node) => {
    const launcher = document.getElementById("welcome-gift-launcher");
    const target = launcher?.getBoundingClientRect();
    // Layout offsets stay stable while Grow applies its initial scale.
    const left = (window.innerWidth - node.offsetWidth) / 2;
    const top = (window.innerHeight - node.offsetHeight) / 2;
    const x = target ? target.left + target.width / 2 : window.innerWidth - 44;
    const y = target ? target.top + target.height / 2 : window.innerHeight - 44;
    node.style.transformOrigin = `${x - left}px ${y - top}px`;
  };
  const [openLogin, setOpenLogin] = useGlobalState("openLogin");
  const [promotion] = useGlobalState("promotion");
  const [welcomePromoEligible] = useGlobalState("welcomePromoEligible");

  const { community, currency, loading: localeLoading } = useUserLocale();
  const [promoCodeCopied, setPromoCodeCopied] = useState(false);
  const welcomePromoAmount = currencyToCreditsGiftMap[currency]?.welcome_promo;
  const formattedWelcomePromoAmount = formatCurrency(welcomePromoAmount, currency);
  const promoCodeByCommunity = {
    sg: "SOWELCOMESG",
    hk: "SOWELCOMEHK",
    tw: "SOWELCOMETW",
    my: "SOWELCOMEMY",
  };
  const promoCode = promoCodeByCommunity[community];
  const welcomePromo = promotion?.promoCodes?.find(
    (promo) => promo.name?.toLowerCase() === promoCode?.toLowerCase()
  );
  const minimumSpend = welcomePromo?.special_conditions?.min_spend;
  const formattedMinimumSpend =
    minimumSpend != null ? formatCurrency(minimumSpend, currency) : null;
  const isLoggedIn = Boolean(localStorage.getItem("JWT_TOKEN"));
  const handleCreateAccount = () => {
    const rerouteUrl = new URL(window.location.href);
    rerouteUrl.searchParams.set("welcome_email", "true");
    const reroute = `${rerouteUrl.pathname}${rerouteUrl.search}${rerouteUrl.hash}`;
    window.location.href = `/login?reroute=${encodeURIComponent(reroute)}`;
  };

  if (
    localeLoading ||
    !promoCode ||
    (isLoggedIn &&
      (welcomePromoEligible !== true || !welcomePromo || formattedMinimumSpend == null))
  ) {
    return null;
  }

  return (
    <Modal
      open={openLogin}
      closeAfterTransition
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      BackdropProps={{ transitionDuration: duration }}
      onClose={() => {
        setOpenLogin(false);
        setPromoCodeCopied(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Grow
        in={openLogin}
        timeout={duration}
        onEnter={anchorTransition}
        easing="cubic-bezier(0.22, 1, 0.36, 1)"
      >
        <Box
          sx={style}
          className="WelcomeGiftModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <button
            className="WelcomeGiftModalClose"
            aria-label={t("welcome_gift_modal.close", { defaultValue: "Close" })}
            onClick={() => {
              setOpenLogin(false);
              setPromoCodeCopied(false);
            }}
          >
            <CloseIcon style={{ fontSize: "19px" }} />
          </button>

          <div className="WelcomeGiftModalContent">
            <img
              className="WelcomeGiftModalPhoto"
              src="/images/welcome-gift.webp"
              alt="Travellers taking a break together on a cycling trip"
              width={960}
              height={637}
            />

            {isLoggedIn ? (
              <>
                <h2 id="modal-modal-title" className="WelcomeGiftModalTitle">
                  {t("welcome_gift_modal.your_promo_code", {
                    defaultValue: "Your welcome promo code",
                  })}
                </h2>
                <p id="modal-modal-description" className="WelcomeGiftModalDescription">
                  {t("welcome_gift_modal.apply_at_checkout", {
                    amount: formattedWelcomePromoAmount,
                    minimumSpend: formattedMinimumSpend,
                    defaultValue:
                      "Apply this code at checkout to save {{amount}} with a minimum spend of {{minimumSpend}}.",
                  })}
                </p>
                <div className="WelcomeGiftPromoCode">{promoCode}</div>
                <CopyToClipboard
                  text={promoCode}
                  onCopy={() => {
                    setPromoCodeCopied(true);
                  }}
                >
                  <Button className="WelcomeGiftModalCta">
                    {promoCodeCopied
                      ? t("welcome_gift_modal.copied", { defaultValue: "Copied!" })
                      : t("welcome_gift_modal.copy_code", { defaultValue: "Copy code" })}
                  </Button>
                </CopyToClipboard>
              </>
            ) : (
              <>
                <h2 id="modal-modal-title" className="WelcomeGiftModalTitle">
                  {t("welcome_gift_modal.title", { defaultValue: "You've got a welcome gift" })}
                </h2>
                <p id="modal-modal-description" className="WelcomeGiftModalDescription">
                  {t("welcome_gift_modal.signup_offer_detail", {
                    defaultValue: "Create an account to reveal your exclusive first-trip reward.",
                  })}
                </p>
                <Button className="WelcomeGiftModalCta" onClick={handleCreateAccount}>
                  {t("welcome_gift_modal.create_account_button", {
                    defaultValue: "Reveal my gift",
                  })}
                </Button>
              </>
            )}
          </div>
        </Box>
      </Grow>
    </Modal>
  );
};
